/**
 * Validation utility functions for WalkCompanion
 */

import { ChallengeInputValidation } from '../types';

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate challenge input value
 */
export function validateChallengeInput(value: number): ValidationResult {
  if (typeof value !== 'number') {
    return { isValid: false, error: 'Value must be a number' };
  }

  if (isNaN(value)) {
    return { isValid: false, error: 'Value must be a valid number' };
  }

  if (value < 0) {
    return { isValid: false, error: 'Value cannot be negative' };
  }

  if (!isFinite(value)) {
    return { isValid: false, error: 'Value must be finite' };
  }

  return { isValid: true };
}

/**
 * Validate challenge input against constraints
 */
export function validateChallengeInputWithConstraints(
  value: number,
  constraints: ChallengeInputValidation
): ValidationResult {
  // First validate basic input
  const basicValidation = validateChallengeInput(value);
  if (!basicValidation.isValid) {
    return basicValidation;
  }

  // Check minimum value
  if (constraints.minValue !== undefined && value < constraints.minValue) {
    return {
      isValid: false,
      error: `Value must be at least ${constraints.minValue}`
    };
  }

  // Check maximum value
  if (constraints.maxValue !== undefined && value > constraints.maxValue) {
    return {
      isValid: false,
      error: `Value must be at most ${constraints.maxValue}`
    };
  }

  // Check step value
  if (constraints.step !== undefined && constraints.step > 0) {
    const remainder = value % constraints.step;
    if (Math.abs(remainder) > 0.001) { // Allow for floating point precision
      return {
        isValid: false,
        error: `Value must be a multiple of ${constraints.step}`
      };
    }
  }

  return { isValid: true };
}

/**
 * Validate email address
 */
export function validateEmail(email: string): ValidationResult {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  return { isValid: true };
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): ValidationResult {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }

  if (password.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters long' };
  }

  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one uppercase letter' };
  }

  // Check for at least one lowercase letter
  if (!/[a-z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one lowercase letter' };
  }

  // Check for at least one number
  if (!/\d/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one number' };
  }

  return { isValid: true };
}

/**
 * Validate display name
 */
export function validateDisplayName(name: string): ValidationResult {
  if (!name) {
    return { isValid: false, error: 'Display name is required' };
  }

  if (name.length < 2) {
    return { isValid: false, error: 'Display name must be at least 2 characters long' };
  }

  if (name.length > 50) {
    return { isValid: false, error: 'Display name must be less than 50 characters' };
  }

  // Check for valid characters (letters, numbers, spaces, hyphens, apostrophes)
  const nameRegex = /^[a-zA-Z0-9\s\-']+$/;
  if (!nameRegex.test(name)) {
    return { isValid: false, error: 'Display name contains invalid characters' };
  }

  return { isValid: true };
}

/**
 * Validate user ID
 */
export function validateUserId(userId: string): ValidationResult {
  if (!userId) {
    return { isValid: false, error: 'User ID is required' };
  }

  if (userId.length < 10) {
    return { isValid: false, error: 'User ID must be at least 10 characters long' };
  }

  if (userId.length > 50) {
    return { isValid: false, error: 'User ID must be less than 50 characters' };
  }

  // Check for valid characters (alphanumeric, hyphens, underscores)
  const userIdRegex = /^[a-zA-Z0-9\-_]+$/;
  if (!userIdRegex.test(userId)) {
    return { isValid: false, error: 'User ID contains invalid characters' };
  }

  return { isValid: true };
}

/**
 * Validate challenge ID
 */
export function validateChallengeId(challengeId: string): ValidationResult {
  if (!challengeId) {
    return { isValid: false, error: 'Challenge ID is required' };
  }

  if (challengeId.length < 3) {
    return { isValid: false, error: 'Challenge ID must be at least 3 characters long' };
  }

  if (challengeId.length > 50) {
    return { isValid: false, error: 'Challenge ID must be less than 50 characters' };
  }

  // Check for valid characters (alphanumeric, hyphens, underscores)
  const challengeIdRegex = /^[a-zA-Z0-9\-_]+$/;
  if (!challengeIdRegex.test(challengeId)) {
    return { isValid: false, error: 'Challenge ID contains invalid characters' };
  }

  return { isValid: true };
}

/**
 * Validate organization ID
 */
export function validateOrganizationId(orgId: string): ValidationResult {
  if (!orgId) {
    return { isValid: false, error: 'Organization ID is required' };
  }

  if (orgId.length < 2) {
    return { isValid: false, error: 'Organization ID must be at least 2 characters long' };
  }

  if (orgId.length > 30) {
    return { isValid: false, error: 'Organization ID must be less than 30 characters' };
  }

  // Check for valid characters (alphanumeric, hyphens, underscores)
  const orgIdRegex = /^[a-zA-Z0-9\-_]+$/;
  if (!orgIdRegex.test(orgId)) {
    return { isValid: false, error: 'Organization ID contains invalid characters' };
  }

  return { isValid: true };
}

/**
 * Validate team ID
 */
export function validateTeamId(teamId: string): ValidationResult {
  if (!teamId) {
    return { isValid: false, error: 'Team ID is required' };
  }

  if (teamId.length < 3) {
    return { isValid: false, error: 'Team ID must be at least 3 characters long' };
  }

  if (teamId.length > 30) {
    return { isValid: false, error: 'Team ID must be less than 30 characters' };
  }

  // Check for valid characters (alphanumeric, hyphens, underscores)
  const teamIdRegex = /^[a-zA-Z0-9\-_]+$/;
  if (!teamIdRegex.test(teamId)) {
    return { isValid: false, error: 'Team ID contains invalid characters' };
  }

  return { isValid: true };
}

/**
 * Validate date string
 */
export function validateDateString(dateString: string): ValidationResult {
  if (!dateString) {
    return { isValid: false, error: 'Date is required' };
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return { isValid: false, error: 'Please enter a valid date' };
  }

  return { isValid: true };
}

/**
 * Validate URL
 */
export function validateURL(url: string): ValidationResult {
  if (!url) {
    return { isValid: false, error: 'URL is required' };
  }

  try {
    new URL(url);
    return { isValid: true };
  } catch {
    return { isValid: false, error: 'Please enter a valid URL' };
  }
}

/**
 * Validate phone number (basic validation)
 */
export function validatePhoneNumber(phone: string): ValidationResult {
  if (!phone) {
    return { isValid: false, error: 'Phone number is required' };
  }

  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  if (digitsOnly.length < 10) {
    return { isValid: false, error: 'Phone number must have at least 10 digits' };
  }

  if (digitsOnly.length > 15) {
    return { isValid: false, error: 'Phone number must have no more than 15 digits' };
  }

  return { isValid: true };
}

/**
 * Validate required field
 */
export function validateRequired(value: any, fieldName: string): ValidationResult {
  if (value === null || value === undefined || value === '') {
    return { isValid: false, error: `${fieldName} is required` };
  }

  return { isValid: true };
}

/**
 * Validate string length
 */
export function validateStringLength(
  value: string,
  minLength: number,
  maxLength: number,
  fieldName: string
): ValidationResult {
  if (!value) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (value.length < minLength) {
    return { isValid: false, error: `${fieldName} must be at least ${minLength} characters long` };
  }

  if (value.length > maxLength) {
    return { isValid: false, error: `${fieldName} must be less than ${maxLength} characters` };
  }

  return { isValid: true };
}

/**
 * Validate number range
 */
export function validateNumberRange(
  value: number,
  min: number,
  max: number,
  fieldName: string
): ValidationResult {
  if (typeof value !== 'number' || isNaN(value)) {
    return { isValid: false, error: `${fieldName} must be a valid number` };
  }

  if (value < min) {
    return { isValid: false, error: `${fieldName} must be at least ${min}` };
  }

  if (value > max) {
    return { isValid: false, error: `${fieldName} must be at most ${max}` };
  }

  return { isValid: true };
}

/**
 * Combine multiple validation results
 */
export function combineValidations(...validations: ValidationResult[]): ValidationResult {
  for (const validation of validations) {
    if (!validation.isValid) {
      return validation;
    }
  }

  return { isValid: true };
}

// Utility to sanitize Firebase keys
export function sanitizeFirebaseKey(key: string): string {
  return key.replace(/[.#$\[\]]/g, '_');
} 